import { env, redactProperties } from '@ircf/config';
import type { AnalyticsEvent, AnalyticsProperties } from '@ircf/config';

/**
 * Privacy-conscious analytics.
 *
 * Two properties matter here. First, the event name must be one of a fixed set,
 * checked by the type system, so a typo cannot silently create a new metric.
 * Second - and this is the important one - every property object is passed
 * through `redactProperties`, which drops any key not on an explicit allow-list.
 * That makes it structurally impossible to leak a health answer, an injury note
 * or a message body into a third-party endpoint, even by accident.
 *
 * With no endpoint configured, `track` is a no-op. Nothing is queued, nothing is
 * stored, and no beacon is attempted.
 */

type Properties = Record<string, unknown>;

interface QueuedEvent {
  event: AnalyticsEvent;
  properties: AnalyticsProperties;
  at: string;
}

const queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const FLUSH_DELAY_MS = 2_000;
const MAX_QUEUE = 25;

function isEnabled(): boolean {
  return env.analyticsEnabled && env.analyticsEndpoint !== null;
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function flush(): Promise<void> {
  if (queue.length === 0 || !isEnabled()) {
    queue.length = 0;
    return;
  }

  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({ appSlug: env.appSlug, events: batch });

  try {
    // `keepalive` lets the final batch survive a page unload.
    await fetch(env.analyticsEndpoint as string, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      // Analytics must never carry the session cookie.
      credentials: 'omit',
      mode: 'cors',
    });
  } catch {
    // Analytics failure is never surfaced and never retried: a dropped metric is
    // strictly preferable to noise in the console or a retry loop.
  }
}

/**
 * Records an event. Property values are redacted against the allow-list before
 * they are queued, so nothing sensitive is ever held in memory awaiting a flush.
 */
export function track(event: AnalyticsEvent, properties: Properties = {}): void {
  const { properties: safe, dropped } = redactProperties(properties);

  if (import.meta.env.DEV && dropped.length > 0) {
    // Loud in development so a disallowed dimension is caught while it is being
    // written, not months later during a privacy review.
    console.warn(
      `[analytics] "${event}" dropped disallowed properties: ${dropped.join(', ')}. ` +
        'Add the key to ALLOWED_PROPERTY_KEYS only if it is genuinely non-sensitive.',
    );
  }

  if (!isEnabled()) return;

  queue.push({ event, properties: safe, at: new Date().toISOString() });

  if (queue.length >= MAX_QUEUE) {
    void flush();
    return;
  }
  scheduleFlush();
}

/** Flushes synchronously on page hide, so the last interaction is not lost. */
export function installAnalyticsLifecycle(): () => void {
  if (!isEnabled()) return () => {};

  const onHide = (): void => {
    if (queue.length === 0) return;
    const batch = queue.splice(0, queue.length);
    navigator.sendBeacon?.(
      env.analyticsEndpoint as string,
      new Blob([JSON.stringify({ appSlug: env.appSlug, events: batch })], {
        type: 'application/json',
      }),
    );
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
  window.addEventListener('pagehide', onHide);

  return () => {
    window.removeEventListener('pagehide', onHide);
  };
}
